import { Maximize2Icon, X } from 'components/icons';
import { Button } from 'components/ui/button';
import { Dialog, DialogClose, DialogContent, DialogTrigger } from 'components/ui/dialog';
import { Tooltip } from 'components/ui/tooltip';
import { type ReactNode, useId } from 'react';
import { useIntl } from 'react-intl';
import { FigureHeader, type FigureHeaderProps } from './FigureFrame';

const ICON_BUTTON_CLASS_NAME =
    'text-foreground-secondary hover:bg-muted hover:text-foreground size-8 rounded-lg new:[&_svg[stroke]]:stroke-[1.5px]';

interface Props {
    header: Omit<FigureHeaderProps, 'actions' | 'titleId'>;
    /** The header's actions before Close: the Figure's Download. */
    actions?: ReactNode;
    /** The Figure's body at full size: the whole table, a taller chart. */
    children: ReactNode;
}

/**
 * Open full size and the dialog it opens: the Figure's own header, Close in place of Open full size, and its body at
 * full size, scrolling inside a dialog that never outgrows the window. Esc and the backdrop close it, and focus goes
 * back to Open full size.
 */
export function FigureDialog({ header, actions, children }: Props) {
    const intl = useIntl();
    const titleId = useId();
    const openLabel = intl.formatMessage({ id: 'ai-chat.figure.open-full-size' });
    const closeLabel = intl.formatMessage({ id: 'ai-chat.figure.close' });

    return (
        <Dialog>
            <Tooltip content={openLabel}>
                <DialogTrigger
                    render={
                        <Button
                            variant="ghost"
                            size="icon-sm"
                            aria-label={openLabel}
                            className={ICON_BUTTON_CLASS_NAME}
                        />
                    }
                >
                    <Maximize2Icon className="size-4" />
                </DialogTrigger>
            </Tooltip>
            <DialogContent
                showCloseButton={false}
                aria-labelledby={header.title ? titleId : undefined}
                className="bg-card text-foreground shadow-dialog flex max-h-[calc(100dvh-4rem)] flex-col gap-0 overflow-y-auto rounded-[20px] p-0 sm:max-w-[calc(100%-2rem)] md:max-w-[calc(100%-10rem)]"
            >
                <div className="@container px-4 pt-4 pb-6">
                    <FigureHeader
                        {...header}
                        titleId={titleId}
                        actions={
                            <>
                                {actions}
                                <Tooltip content={closeLabel}>
                                    <DialogClose
                                        render={
                                            <Button
                                                variant="ghost"
                                                size="icon-sm"
                                                aria-label={closeLabel}
                                                className={ICON_BUTTON_CLASS_NAME}
                                            />
                                        }
                                    >
                                        <X className="size-4" />
                                    </DialogClose>
                                </Tooltip>
                            </>
                        }
                    />
                    {children}
                </div>
            </DialogContent>
        </Dialog>
    );
}
